import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';

import {
  CLINIC_SPECIALTIES,
  CLINIC_TYPES,
  type ClinicSpecialty,
  type ClinicType,
  CONTACT_TYPES,
  DISTANCE_TYPES,
  type DistanceType,
  GROUP_DOCUMENT_TYPES,
  type GroupDocumentType,
  INTAKE_JOB_STATUSES,
  type IntakeJobStatus,
  NETWORK_STATUSES,
  NOTE_TYPES,
  PROCEDURE_CATEGORIES,
  PROVIDER_GROUP_STATUSES,
  REPORT_STATUSES,
  type ReportStatus,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
} from '@backend/db/const/db.const.ENUMS';
import {
  type Address,
  addressSchema,
  analyticsReportResults,
  analyticsReports,
  clinicIdCounters,
  clinicProviders,
  clinics,
  componentObjectSchema,
  type Contact,
  contracts,
  type Features,
  featuresSchema,
  groups,
  intakeJobs,
  notes,
  openingHoursSchema,
  optionalAddressSchema,
  procedures,
  providerDocuments,
  providers,
  type RosterExtractedProvider,
  rosterJobs,
  tickets,
  tinInfoSchema,
  users,
} from '@backend/db/schema/tables';

// User role type - ephemeral, only in JWT token
export type UserRole = 'Admin' | 'PNOps' | 'ViewOnly' | null;

// Drizzle inferred types (single source of truth)
export type Procedure = InferSelectModel<typeof procedures>;
export type NewProcedure = InferInsertModel<typeof procedures>;
export type ProviderGroup = InferSelectModel<typeof groups>;
export type NewProviderGroup = InferInsertModel<typeof groups>;
export type Clinic = InferSelectModel<typeof clinics>;
export type NewClinic = InferInsertModel<typeof clinics>;
export type Note = InferSelectModel<typeof notes>;
export type NewNote = InferInsertModel<typeof notes>;
export type ClinicIdCounter = InferSelectModel<typeof clinicIdCounters>;
export type NewClinicIdCounter = InferInsertModel<typeof clinicIdCounters>;
export type Ticket = InferSelectModel<typeof tickets>;
export type NewTicket = InferInsertModel<typeof tickets>;
export type Contract = InferSelectModel<typeof contracts>;
export type NewContract = InferInsertModel<typeof contracts>;
export type User = InferSelectModel<typeof users>;
export type NewUser = InferInsertModel<typeof users>;
export type IntakeJob = InferSelectModel<typeof intakeJobs>;
export type NewIntakeJob = InferInsertModel<typeof intakeJobs>;
export type AnalyticsReport = InferSelectModel<typeof analyticsReports>;
export type NewAnalyticsReport = InferInsertModel<typeof analyticsReports>;
export type AnalyticsReportResult = InferSelectModel<
  typeof analyticsReportResults
>;
export type NewAnalyticsReportResult = InferInsertModel<
  typeof analyticsReportResults
>;
export type { TinInfo } from '@backend/db/schema/tables';

// Opening hours types and schemas
export type {
  DaySchedule,
  WeeklySchedule,
  ServiceHours,
  OpeningHours,
} from '@backend/db/schema/tables';
export {
  dayScheduleSchema,
  weeklyScheduleSchema,
  serviceHoursSchema,
  openingHoursSchema,
} from '@backend/db/schema/tables';

// drizzle-zod generated schemas (automatically synced with database schema)
export const insertProcedureSchema = createInsertSchema(procedures);
export const selectProcedureSchema = createSelectSchema(procedures);

export const insertUserSchema = createInsertSchema(users);
export const selectUserSchema = createSelectSchema(users);

// Create customized schemas for specific use cases
export const createProcedureSchema = insertProcedureSchema.omit({
  createdAt: true,
  updatedAt: true,
});

export const updateProcedureSchema = insertProcedureSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .partial()
  .required({ id: true });

export const createUserSchema = insertUserSchema.omit({
  createdAt: true,
  updatedAt: true,
});

export const updateUserSchema = insertUserSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .partial()
  .required({ id: true });

// User directory types for lightweight user lookup
export type UserDirectoryItem = Pick<
  User,
  'id' | 'name' | 'email' | 'isActive'
>;

export const userDirectoryItemSchema = selectUserSchema.pick({
  id: true,
  name: true,
  email: true,
  isActive: true,
});

// Search and filter schemas
export const procedureSearchSchema = z.object({
  search: z.string().optional(),
  category: z.enum(PROCEDURE_CATEGORIES).optional(),
  onFacility: z.boolean().optional(),
  limit: z.number().min(1).max(10000).default(20),
  offset: z.number().min(0).default(0),
  sortBy: z
    .enum(['title', 'id', 'cptCode', 'category', 'standardRate', 'createdAt'])
    .default('title'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

// Inferred types from schemas (single source of truth)
// Using any cast to work around TypeScript ZodType constraint issues
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CreateProcedureInput = z.infer<typeof createProcedureSchema & any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UpdateProcedureInput = z.infer<typeof updateProcedureSchema & any>;
export type ProcedureSearchInput = z.infer<typeof procedureSearchSchema>;

// API response types
export type ProcedureListItem = {
  id: string;
  cptCode: string | null;
  title: string;
  description: string | null;
  category: string;
  standardRate: string;
  onFacility: boolean;
};

export type ProcedureSearchResult = {
  procedures: ProcedureListItem[];
  total: number;
  hasMore: boolean;
};

// ========== PROVIDER NETWORK SCHEMAS ==========

// Manual schemas for provider groups (avoiding drizzle-zod issues with JSONB)
export const createProviderGroupSchema = z.object({
  groupName: z.string().min(1, 'Group name is required'),
  status: z.enum(PROVIDER_GROUP_STATUSES).default('lead'),
  networkStatus: z.enum(NETWORK_STATUSES).nullable().optional(),
  groupAddress: addressSchema.optional(),
  features: featuresSchema.optional(),
  openingHours: openingHoursSchema.optional(),
  contacts: z
    .array(
      z.object({
        id: z.string().min(1, 'Contact ID is required'),
        name: z.string().min(1, 'Contact name is required'),
        title: z.string().optional(),
        type: z.array(z.enum(CONTACT_TYPES)),
        officePhone: z.string().optional(),
        mobilePhone: z.string().optional(),
        fax: z.string().optional(),
        email: z.union([z.string().email(), z.literal('')]).optional(),
        address: addressSchema.optional(),
        active: z.boolean().default(true),
        notes: z.string().optional(),
      }),
    )
    .optional(),
});

// Override status to remove .default('lead') — partial updates must not inject defaults
// groupAddress uses optionalAddressSchema so empty/partial addresses don't fail validation
export const updateProviderGroupSchema = createProviderGroupSchema
  .omit({ status: true, groupAddress: true })
  .partial()
  .extend({
    id: z.number().int().positive(),
    status: z.enum(PROVIDER_GROUP_STATUSES).optional(),
    groupAddress: addressSchema.optional().or(optionalAddressSchema),
  });

// Manual schemas for clinics
export const createClinicSchema = z.object({
  groupId: z.number().int().positive().optional(),
  id: z.string().min(1, 'Clinic ID is required'),
  clinicName: z.string().min(1, 'Clinic name is required'),
  status: z.enum(PROVIDER_GROUP_STATUSES).default('lead'),
  phone: z.string().optional(),
  fax: z.string().optional(),
  email: z.union([z.string().email(), z.literal('')]).optional(),
  type: z.enum(CLINIC_TYPES).default('Clinic'),
  specialties: z.array(z.enum(CLINIC_SPECIALTIES)).optional(),
  features: featuresSchema.optional(),
  openingHours: openingHoursSchema.nullable().optional(),
  contacts: z
    .array(
      z.object({
        id: z.string().min(1, 'Contact ID is required'),
        name: z.string().min(1, 'Contact name is required'),
        title: z.string().optional(),
        type: z.array(z.enum(CONTACT_TYPES)),
        officePhone: z.string().optional(),
        mobilePhone: z.string().optional(),
        fax: z.string().optional(),
        email: z.union([z.string().email(), z.literal('')]).optional(),
        address: addressSchema.optional(),
        active: z.boolean().default(true),
        notes: z.string().optional(),
      }),
    )
    .optional(),
  // Physical address validation fields (optional for creation)
  physicalText: z.string().optional(),
  physicalLat: z.string().optional(), // decimal as string
  physicalLong: z.string().optional(), // decimal as string
  physicalVerified: addressSchema
    .extend({
      lat: z.number().optional(),
      lng: z.number().optional(),
    })
    .optional(),
  // Formatted address from geocoding API (used to update physicalText after verification)
  formattedAddress: z.string().optional(),
  // Simple JSONB address fields (no validation)
  shippingAddress: optionalAddressSchema,
  billingAddress: optionalAddressSchema,
  // Components field for clinic-specific component assignments
  components: z.record(z.string(), componentObjectSchema).optional(),
  // Network status: in-network, out-of-network, or null (unspecified)
  networkStatus: z.enum(NETWORK_STATUSES).nullable().optional(),
});

// Create a schema for clinic creation without clinic ID (auto-generated)
export const createClinicWithoutCodeSchema = createClinicSchema.omit({
  id: true,
});

// Override status and type to remove .default(...) — partial updates must not
// inject defaults, otherwise an edit that doesn't change status silently
// writes 'lead' (and similarly 'Clinic' for type) back to the row.
export const updateClinicSchema = createClinicSchema
  .omit({ status: true, type: true })
  .partial()
  .extend({
    id: z.string(),
    status: z.enum(PROVIDER_GROUP_STATUSES).optional(),
    type: z.enum(CLINIC_TYPES).optional(),
  });

// NOTE: providerNetworkSearchSchema is co-located in trpc.procedure.providerNetwork.searchProviderNetwork.ts
// Do not define input schemas here - they should be co-located with their procedures

// Feature value schemas for three-state system
// Boolean features: true (enabled), false (disabled), null/undefined (unknown)
export const booleanFeatureValueSchema = z.boolean().nullable().optional();

// String features: non-empty string (enabled), empty string (disabled), null/undefined (unknown)
export const stringFeatureValueSchema = z.string().nullable().optional();

// Note: The actual Features type is defined in schema/tables.ts using featuresSchema
// These schemas are for reference and validation purposes
// Features are stored directly in JSONB as actual values, not as enums
// Inheritance logic (for clinics in groups) is handled at the application layer:
//   - undefined = inherit from group (no value stored in clinic.features)
//   - null = explicitly unknown (stored in clinic.features)
//   - Boolean features: true (enabled) / false (disabled)
//   - String features: non-empty string (enabled) / empty string (disabled)

// Component info type for clinic list
export type ClinicComponentInfo = {
  componentId: string;
  onContract: boolean;
  rate?: number;
  status?: 'enabled' | 'disabled' | 'unknown' | 'notOnContract';
  suspended?: boolean;
  suspendDetails?: {
    reason: string;
    from: string;
    until?: string;
  };
};

// Feature info type for clinic list
export type ClinicFeatureInfo = {
  featureId: string;
  featureLabel: string;
  status: 'enabled' | 'disabled' | 'unknown';
  value?: string | boolean | null;
  isOverride: boolean;
  groupValue?: string | boolean | null;
  groupStatus?: 'enabled' | 'disabled' | 'unknown';
};

// API response types
export type ClinicListItem = {
  id: string;
  clinicName: string;
  status: 'lead' | 'active' | 'onHold' | 'inactive' | 'billing' | 'recruitment';
  groupId: number | null;
  groupName: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  type: ClinicType;
  specialties: ClinicSpecialty[] | null;
  physicalVerified: Address | null;
  // Geographic coordinates for map functionality
  physicalLat: string | null;
  physicalLong: string | null;
  tinNumber: string | null;
  networkStatus: 'in' | 'out' | null;
  contacts: Contact[] | null;
  createdAt: string; // ISO 8601 date string from JSON serialization
  // Component information (populated when componentIds filter is used)
  componentInfo?: ClinicComponentInfo[];
  // Feature information (populated when featureIds filter is used)
  featureInfo?: ClinicFeatureInfo[];
};

export type ProviderGroupListItem = {
  id: number;
  groupName: string;
  status: 'lead' | 'active' | 'onHold' | 'inactive' | 'billing' | 'recruitment';
  networkStatus: 'in' | 'out' | null;
  clinicCount: number;
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  } | null;
};

export type ProviderNetworkSearchResult = {
  clinics: ClinicListItem[];
  total: number;
  hasMore: boolean;
};

export type ProviderGroupSearchResult = {
  groups: ProviderGroupListItem[];
  total: number;
  hasMore: boolean;
};

// Input types
export type CreateProviderGroupInput = z.infer<
  typeof createProviderGroupSchema
>;
export type UpdateProviderGroupInput = z.infer<
  typeof updateProviderGroupSchema
>;
export type CreateClinicInput = z.infer<typeof createClinicSchema>;
export type CreateClinicWithoutCodeInput = z.infer<
  typeof createClinicWithoutCodeSchema
>;
export type UpdateClinicInput = z.infer<typeof updateClinicSchema>;
// NOTE: ProviderNetworkSearchInput type is inferred in the procedure file where the schema is co-located

// ========== NOTE SCHEMAS ==========

// Manual note schemas to handle string dates properly
export const createNoteSchema = z.object({
  // userId removed - backend populates from ctx.user
  pocId: z.string().nullable().optional(),
  pocName: z.string().nullable().optional(),
  noteType: z.enum(NOTE_TYPES),
  description: z.string(),
  entryDateTime: z.string().datetime(),
  clinicId: z.string().nullable().optional(),
  groupId: z.number().int().positive().nullable().optional(),
});

export const updateNoteSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().optional(),
  pocId: z.string().nullable().optional(),
  pocName: z.string().nullable().optional(),
  noteType: z.enum(NOTE_TYPES).optional(),
  description: z.string().optional(),
  entryDateTime: z.string().datetime().optional(),
  clinicId: z.string().nullable().optional(),
  groupId: z.number().int().positive().nullable().optional(),
});

// Keep the original drizzle schemas for internal use
export const insertNoteSchema = createInsertSchema(notes);
export const selectNoteSchema = createSelectSchema(notes);

// Note search schema - simplified to only include scoping
export const noteSearchSchema = z.object({
  clinicId: z.string().optional(),
  groupId: z.number().int().positive().optional(),
});

// Manual type definitions for better type safety
export type CreateNoteInput = {
  userId: string;
  pocId?: string | null;
  pocName?: string | null;
  noteType: 'Phone' | 'Email' | 'Fax' | 'Other';
  description: string;
  entryDateTime: string;
  clinicId?: string | null;
  groupId?: number | null;
};

export type UpdateNoteInput = {
  id: string;
  userId?: string;
  pocId?: string | null;
  pocName?: string | null;
  noteType?: 'Phone' | 'Email' | 'Fax' | 'Other';
  description?: string;
  entryDateTime?: string;
  clinicId?: string | null;
  groupId?: number | null;
};

export type NoteSearchInput = {
  clinicId?: string;
  groupId?: number;
};

// API response types
export type NoteListItem = {
  id: string;
  userId: string;
  pocId: string | null;
  pocName: string | null;
  noteType: 'Phone' | 'Email' | 'Fax' | 'Other';
  description: string;
  entryDateTime: Date;
  createdAt: Date;
};

export type NoteSearchResult = {
  notes: NoteListItem[];
  total: number;
  hasMore: boolean;
};

export type NoteExportData = {
  'Date/Time': string;
  User: string;
  POC: string;
  Type: string;
  Description: string;
};

// ========== DOCUMENT SCHEMAS ==========

// Re-export document schema from tables
export { documentSchema, tinInfoSchema } from '@backend/db/schema/tables';
export type { Document } from '@backend/db/schema/tables';

// Re-export GroupDocumentType from ENUMS
export type { GroupDocumentType } from '@backend/db/const/db.const.ENUMS';

// Document management schemas
export const createDocumentSchema = z.object({
  documentType: z.enum(GROUP_DOCUMENT_TYPES),
  customDocumentType: z.string().optional(),
  documentName: z.string().optional(),
  effectiveDate: z.string().min(1, 'Effective date is required'),
  fileName: z.string().min(1, 'File name is required'),
  fileSize: z.number().positive().optional(),
  mimeType: z.string().optional(),
});

export const uploadDocumentSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    documentId: z.string().min(1, 'Document ID is required'),
    documentType: z.enum(GROUP_DOCUMENT_TYPES),
    customDocumentType: z.string().optional(),
    documentName: z.string().optional(),
    effectiveDate: z.string().min(1, 'Effective date is required'),
    fileName: z.string().min(1, 'File name is required'),
    fileUrl: z.string().url('Valid file URL is required'),
    s3Key: z.string().min(1, 'S3 key is required'),
    fileSize: z.number().positive().optional(),
    mimeType: z.string().optional(),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

export const deleteDocumentSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    documentId: z.string().min(1, 'Document ID is required'),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

export const restoreDocumentSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    documentId: z.string().min(1, 'Document ID is required'),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

export const updateDocumentSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    documentId: z.string().min(1, 'Document ID is required'),
    documentType: z.enum(GROUP_DOCUMENT_TYPES).optional(),
    customDocumentType: z.string().optional(),
    documentName: z.string().optional(),
    effectiveDate: z.string().optional(),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

export const getDocumentsSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    includeDeleted: z.boolean().optional().default(false),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

// Get document signed URL schema
export const getDocumentSignedUrlSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    documentId: z.string().min(1, 'Document ID is required'),
    type: z.enum(['download', 'preview']).default('download'),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

// Document input/output types
export type CreateDocumentInput = z.infer<typeof createDocumentSchema>;
export type UploadDocumentInput = z.infer<typeof uploadDocumentSchema>;
export type DeleteDocumentInput = z.infer<typeof deleteDocumentSchema>;
export type RestoreDocumentInput = z.infer<typeof restoreDocumentSchema>;
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>;
export type GetDocumentsInput = z.infer<typeof getDocumentsSchema>;
export type GetDocumentSignedUrlInput = z.infer<
  typeof getDocumentSignedUrlSchema
>;

// Presigned upload URL schema
export const getUploadPresignedUrlSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
    fileName: z.string().min(1, 'File name is required'),
    contentType: z.string().min(1, 'Content type is required'),
    contentLength: z.number().positive('Content length must be positive'),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

export type GetUploadPresignedUrlInput = z.infer<
  typeof getUploadPresignedUrlSchema
>;

export type UploadPresignedUrlResponse = {
  uploadUrl: string;
  uploadFields?: Record<string, string>;
  s3Key: string;
  fileUrl: string;
  documentId: string;
};

export type DocumentListItem = {
  id: string;
  documentType: GroupDocumentType;
  customDocumentType?: string;
  documentName?: string;
  effectiveDate: string;
  uploadDate: string;
  uploadedBy: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  s3Key?: string; // S3 key for the file
  deletedAt?: string; // ISO date string when deleted
  deletedBy?: string; // User ID who deleted the document
};

// ========== TICKET SCHEMAS ==========

// Re-export subtask schema from tables
export { subtaskSchema } from '@backend/db/schema/tables';
export type { Subtask } from '@backend/db/schema/tables';

// drizzle-zod generated schemas
export const insertTicketSchema = createInsertSchema(tickets);
export const selectTicketSchema = createSelectSchema(tickets);

// Create customized schemas for specific use cases
export const createTicketSchema = insertTicketSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateTicketSchema = insertTicketSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .partial()
  .required({ id: true });

// Ticket search schema
export const ticketSearchSchema = z.object({
  search: z.string().optional(),
  status: z.enum(TICKET_STATUSES).optional(),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  clinicId: z.string().optional(),
  groupId: z.number().int().positive().optional(),
  assigneeId: z.string().optional(),
  limit: z.number().min(1).max(10000).default(50),
  offset: z.number().min(0).default(0),
  sortBy: z
    .enum(['title', 'dueDate', 'priority', 'status', 'createdAt'])
    .default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// Manual type definitions for better type safety
export type CreateTicketInput = {
  title: string;
  description?: string | null;
  status?: 'todo' | 'in_progress' | 'done';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string | null;
  assigneeId?: string | null;
  clinicId?: string | null;
  groupId?: number | null;
  subtasks?: {
    id: string;
    title: string;
    completed: boolean;
    createdAt: string;
    updatedAt: string;
  }[];
  createdBy: string;
};

export type UpdateTicketInput = {
  id: string;
  title?: string;
  description?: string | null;
  status?: 'todo' | 'in_progress' | 'done';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string | null;
  assigneeId?: string | null;
  clinicId?: string | null;
  groupId?: number | null;
  subtasks?: {
    id: string;
    title: string;
    completed: boolean;
    createdAt: string;
    updatedAt: string;
  }[];
};

export type TicketSearchInput = {
  search?: string;
  status?: 'todo' | 'in_progress' | 'done';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  clinicId?: string;
  groupId?: number;
  assigneeId?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'title' | 'dueDate' | 'priority' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
};

// API response types
export type TicketListItem = {
  id: string;
  title: string;
  description: string | null;
  status: 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string | null;
  assigneeId: string | null;
  clinicId: string | null;
  clinicName?: string | null;
  groupId: number | null;
  groupName?: string | null;
  subtasks: {
    id: string;
    title: string;
    completed: boolean;
    createdAt: string;
    updatedAt: string;
  }[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
};

export type TicketSearchResult = {
  tickets: TicketListItem[];
  total: number;
  hasMore: boolean;
};

// ========== CONTRACT SCHEMAS ==========

// Contract schemas - removed createContractSchema as part of simplifying contract creation

// Schema for creating an empty draft contract
export const createEmptyDraftContractSchema = z
  .object({
    groupId: z.number().int().positive().optional(),
    clinicId: z.string().optional(),
  })
  .refine((data) => data.groupId || data.clinicId, {
    message: 'Either groupId or clinicId must be provided',
    path: ['groupId', 'clinicId'],
  });

// Schema for updating an existing contract
export const updateContractSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1, 'Contract title is required').optional(),
  sentDate: z.string().optional(),
  receivedDate: z.string().optional(),
  effectiveDate: z.string().min(1, 'Effective date is required').optional(),
  endDate: z.string().optional(),
  paymentTerms: z.number().int().positive().optional(),
  isEvergreen: z.boolean().optional(),
  tins: z.record(z.string(), tinInfoSchema).optional(),
  componentRates: z.record(z.string(), z.number()).optional(),
});

// Schema for activating a contract
export const activateContractSchema = z.object({
  id: z.string().uuid(),
});

export const deleteContractSchema = z.object({
  id: z.string().uuid(),
});

export const getContractsByGroupIdSchema = z.object({
  groupId: z.number().int().positive(),
});

export const getContractsByClinicIdSchema = z.object({
  clinicId: z.string(),
});

export const setClinicContractSchema = z.object({
  clinicId: z.string(),
  contractId: z.string().uuid().nullable().optional(),
  tinNumber: z.string().nullable().optional(),
});

// Contract input/output types - removed CreateContractInput as part of simplifying contract creation
export type CreateEmptyDraftContractInput = z.infer<
  typeof createEmptyDraftContractSchema
>;
export type UpdateContractInput = z.infer<typeof updateContractSchema>;
export type ActivateContractInput = z.infer<typeof activateContractSchema>;
export type DeleteContractInput = z.infer<typeof deleteContractSchema>;
export type GetContractsByGroupIdInput = z.infer<
  typeof getContractsByGroupIdSchema
>;
export type GetContractsByClinicIdInput = z.infer<
  typeof getContractsByClinicIdSchema
>;
export type SetClinicContractInput = z.infer<typeof setClinicContractSchema>;

// API response types
export type ContractListItem = {
  id: string;
  title: string;
  sentDate: string | null;
  receivedDate: string | null;
  effectiveDate: string;
  endDate: string | null;
  paymentTerms: number;
  isEvergreen: boolean;
  status: 'draft' | 'active';
  groupId: number | null;
  clinicId: string | null;
  tinCount: number;
  clinicCount: number;
  componentCount: number;
  createdAt: Date;
};

// Contract component type for detailed view
export type ContractComponent = {
  id: string;
  title: string;
  rate: number;
  standardRate: number;
};

// Contract clinic type for detailed view
export type ContractClinic = {
  id: string;
  clinicName: string;
  tinNumber: string | null;
  physicalAddress?: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
};

// Contract detail type
export type ContractDetail = ContractListItem & {
  tins: Record<
    string,
    { name: string; businessName?: string; description?: string }
  >;
  components: ContractComponent[];
  clinics: ContractClinic[];
};

// ========== INTAKE JOB SCHEMAS ==========

// drizzle-zod generated schemas
export const insertIntakeJobSchema = createInsertSchema(intakeJobs);
export const selectIntakeJobSchema = createSelectSchema(intakeJobs);

// Create customized schemas for specific use cases
export const createIntakeJobSchema = insertIntakeJobSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateIntakeJobSchema = insertIntakeJobSchema
  .omit({
    createdAt: true,
    updatedAt: true,
  })
  .partial()
  .required({ id: true });

// Intake job search schema
export const intakeJobSearchSchema = z.object({
  contractId: z.string().uuid().optional(),
  status: z.enum(INTAKE_JOB_STATUSES).optional(),
  limit: z.number().min(1).max(10000).default(20),
  offset: z.number().min(0).default(0),
  sortBy: z
    .enum(['fileName', 'status', 'createdAt', 'updatedAt'])
    .default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// Manual type definitions for better type safety
export type CreateIntakeJobInput = {
  contractId: string;
  status?: IntakeJobStatus;
  rates?: { ltsCode: string; rate: number }[];
  fileName: string;
  s3Key: string;
  errorMessage?: string | null;
};

export type UpdateIntakeJobInput = {
  id: string;
  contractId?: string;
  status?: IntakeJobStatus;
  rates?: { ltsCode: string; rate: number }[];
  fileName?: string;
  s3Key?: string;
  errorMessage?: string | null;
};

export type IntakeJobSearchInput = {
  contractId?: string;
  status?: IntakeJobStatus;
  limit?: number;
  offset?: number;
  sortBy?: 'fileName' | 'status' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
};

// API response types
export type IntakeJobListItem = {
  id: string;
  contractId: string;
  status: IntakeJobStatus;
  rates: { ltsCode: string; rate: number }[];
  fileName: string;
  s3Key: string;
  errorMessage: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export type IntakeJobSearchResult = {
  intakeJobs: IntakeJobListItem[];
  total: number;
  hasMore: boolean;
};

// ========== ANALYTICS REPORT SCHEMAS ==========

// drizzle-zod generated schemas
export const insertAnalyticsReportSchema = createInsertSchema(analyticsReports);
export const selectAnalyticsReportSchema = createSelectSchema(analyticsReports);
export const insertAnalyticsReportResultSchema = createInsertSchema(
  analyticsReportResults,
);
export const selectAnalyticsReportResultSchema = createSelectSchema(
  analyticsReportResults,
);

// Geocoded address schema (reusable)
export const geocodedAddressSchema = z.object({
  original: z.string().min(1, 'Original address is required'),
  formattedAddress: z.string().min(1, 'Formatted address is required'),
  lat: z.number().min(-90).max(90, 'Latitude must be between -90 and 90'),
  lng: z.number().min(-180).max(180, 'Longitude must be between -180 and 180'),
});

// Create analytics report schema (only addresses and distance)
export const createAnalyticsReportSchema = z.object({
  reportName: z.string().min(1, 'Report name is required'),
  addresses: z
    .array(geocodedAddressSchema)
    .min(1, 'At least one address is required'),
  distanceType: z.enum(DISTANCE_TYPES),
  maxDistance: z
    .number()
    .int()
    .min(1, 'Distance must be at least 1 mile')
    .max(500, 'Distance cannot exceed 500 miles'),
});

// Analytics report filters schema (for frontend filtering)
export const analyticsReportFiltersSchema = z.object({
  groupNames: z.array(z.string()).optional(),
  excludeGroupNames: z.array(z.string()).optional(),
  clinicTypes: z.array(z.enum(CLINIC_TYPES)).optional(),
  specialties: z.array(z.enum(CLINIC_SPECIALTIES)).optional(),
  statuses: z.array(z.enum(PROVIDER_GROUP_STATUSES)).optional(),
  features: featuresSchema.optional(),
});

// Infer geocoded address type
export type GeocodedAddress = z.infer<typeof geocodedAddressSchema>;
export type CreateAnalyticsReportInput = z.infer<
  typeof createAnalyticsReportSchema
>;

// Get report status schema
export const getReportStatusSchema = z.object({
  reportId: z.string().uuid('Invalid report ID'),
});

// List reports schema
export const listReportsSchema = z.object({
  limit: z.number().min(1).max(100).default(20),
  offset: z.number().min(0).default(0),
  status: z.enum(REPORT_STATUSES).optional(),
  sortBy: z
    .enum(['reportName', 'createdAt', 'completedAt'])
    .default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// Infer filter types from schema
export type AnalyticsReportFilters = z.infer<
  typeof analyticsReportFiltersSchema
>;

export type GetReportStatusInput = {
  reportId: string;
};

export type ListReportsInput = {
  limit?: number;
  offset?: number;
  status?: ReportStatus;
  sortBy?: 'reportName' | 'createdAt' | 'completedAt';
  sortOrder?: 'asc' | 'desc';
};

// API response types
export type AnalyticsReportListItem = {
  id: string;
  reportName: string;
  status: ReportStatus;
  distanceType: DistanceType;
  maxDistance: number;
  totalAddresses: number;
  processedAddresses: number;
  progress: number; // percentage 0-100
  fileName: string | null;
  errorMessage: string | null;
  startedAt: Date | null;
  completedAt: Date | null;
  createdAt: Date;
  canDownload: boolean;
};

export type AnalyticsReportStatus = {
  id: string;
  reportName: string;
  status: ReportStatus;
  progress: number;
  totalAddresses: number;
  processedAddresses: number;
  errorMessage: string | null;
  startedAt: Date | null;
  completedAt: Date | null;
  downloadUrl: string | null;
};

export type AnalyticsReportSearchResult = {
  reports: AnalyticsReportListItem[];
  total: number;
  hasMore: boolean;
};

// Enriched analytics report result with dynamically loaded clinic details
export type EnrichedAnalyticsReportResult = {
  id: string;
  inputAddress: string;
  geocodedAddress: {
    formattedAddress: string;
    lat: number;
    lng: number;
  } | null;
  clinicId: string | null;
  clinicName: string | null;
  clinicAddress: string | null;
  distance: number | null; // distance in miles
  drivingTime: number | null; // driving time in minutes
  // Dynamically loaded clinic details
  clinicPhone: string | null;
  clinicEmail: string | null;
  clinicSpecialties: string[] | null;
  clinicStatus: string | null;
  clinicType: string | null;
  clinicNetworkStatus: string | null;
  clinicFeatures: Features | null;
  clinicGroupName: string | null;
  clinicLat: number | null;
  clinicLng: number | null;
  clinicAddressComponents: Address | null; // Parsed address components
};

// Legacy type for backward compatibility (Excel generation)
export type AnalyticsReportResultRow = {
  inputAddress: string;
  geocodedAddress: string | null;
  clinicName: string | null;
  clinicId: string | null;
  clinicAddress: string | null;
  clinicPhone: string | null;
  clinicEmail: string | null;
  clinicSpecialties: string | null; // JSON string of array
  clinicStatus: string | null;
  distance: string | null; // distance in miles
  drivingTime: number | null; // driving time in minutes
};

// ========== CREDENTIALING TYPES ==========

// ========== CREDENTIALING JSONB SCHEMAS ==========

// Provider License schema (stored in providers.licenses JSONB array)
export const providerLicenseSchema = z.object({
  id: z.string(),
  state: z.string().length(2, 'State must be 2 characters'),
  licenseNumber: z.string().min(1, 'License number is required'),
  licenseType: z.string().optional(),
  issueDate: z.string().optional(), // ISO 8601 date string
  expirationDate: z.string().optional(), // ISO 8601 date string - critical for blocking
  status: z.enum(['active', 'expired', 'suspended']).default('active'),
  verifiedDate: z.string().optional(), // ISO 8601 date string
  verifiedBy: z.string().optional(),
  notes: z.string().optional(),
  source: z
    .enum(['manual', 'npi_enrichment', 'roster_upload'])
    .default('manual'),
});

// Malpractice Insurance schema (stored in providers.malpracticeInsurance JSONB)
export const malpracticeInsuranceSchema = z.object({
  carrier: z.string().min(1, 'Carrier is required'),
  policyNumber: z.string().min(1, 'Policy number is required'),
  coverageAmount: z.string().optional(),
  effectiveDate: z.string().min(1, 'Effective date is required'), // ISO 8601 date string
  expirationDate: z.string().min(1, 'Expiration date is required'), // ISO 8601 date string - critical for blocking
  jurisdictions: z.array(z.string()).default([]),
  status: z.enum(['active', 'expired', 'pending']).default('active'),
  verifiedDate: z.string().optional(), // ISO 8601 date string
  verifiedBy: z.string().optional(),
  notes: z.string().optional(),
});

// Practice Location schema (stored in providers.practiceLocations JSONB array)
export const practiceLocationSchema = z.object({
  id: z.string(),
  state: z.string().length(2, 'State must be 2 characters'),
  city: z.string().min(1, 'City is required'),
  address: z.string().optional(),
  isPrimary: z.boolean().default(false),
});

// Privilege Scope schema (stored in providers.privilegeScope JSONB)
export const privilegeScopeSchema = z.object({
  procedures: z.array(z.string()).default([]),
  limitations: z.string().optional(),
  grantingAuthority: z.string().optional(),
  effectiveDate: z.string().optional(), // ISO 8601 date string
  expirationDate: z.string().optional(), // ISO 8601 date string
});

// Federal Portability schema (stored in providers.federalPortability JSONB)
export const federalPortabilitySchema = z.object({
  hasPortability: z.boolean(),
  title10Usc1094d2: z.boolean().default(false),
  documentationUrl: z.string().optional(),
  effectiveDate: z.string().optional(), // ISO 8601 date string
  expirationDate: z.string().optional(), // ISO 8601 date string
});

// Infer types from JSONB schemas
export type ProviderLicense = z.infer<typeof providerLicenseSchema>;
export type MalpracticeInsurance = z.infer<typeof malpracticeInsuranceSchema>;
export type PracticeLocation = z.infer<typeof practiceLocationSchema>;
export type PrivilegeScope = z.infer<typeof privilegeScopeSchema>;
export type FederalPortability = z.infer<typeof federalPortabilitySchema>;

// Drizzle inferred types
export type Provider = InferSelectModel<typeof providers>;
export type NewProvider = InferInsertModel<typeof providers>;
export type ClinicProvider = InferSelectModel<typeof clinicProviders>;
export type NewClinicProvider = InferInsertModel<typeof clinicProviders>;
export type ProviderDocument = InferSelectModel<typeof providerDocuments>;
export type NewProviderDocument = InferInsertModel<typeof providerDocuments>;

// REMOVED: Old table types (providerLicenses, providerInsurance, credentialDocuments, credentialAuditLog, etc.)

// drizzle-zod generated schemas
export const insertProviderSchema = createInsertSchema(providers);
export const selectProviderSchema = createSelectSchema(providers);
export const updateProviderSchema = insertProviderSchema
  .omit({ createdAt: true, updatedAt: true })
  .partial()
  .required({ id: true });

// Provider CRUD schemas
export const updateProviderWithCredentialsSchema = insertProviderSchema
  .omit({ createdAt: true, updatedAt: true })
  .partial()
  .required({ id: true })
  .extend({
    licenses: z.array(providerLicenseSchema).optional(),
    malpracticeInsurance: malpracticeInsuranceSchema.optional(),
    practiceLocations: z.array(practiceLocationSchema).optional(),
    privilegeScope: privilegeScopeSchema.optional(),
    federalPortability: federalPortabilitySchema.optional(),
  });

// Clinic-Provider junction table schemas
export const insertClinicProviderSchema = createInsertSchema(clinicProviders);
export const updateClinicProviderSchema = insertClinicProviderSchema
  .omit({ createdAt: true, updatedAt: true })
  .partial()
  .required({ id: true });

// Provider Documents schemas
export const insertProviderDocumentSchema =
  createInsertSchema(providerDocuments);
export const selectProviderDocumentSchema =
  createSelectSchema(providerDocuments);
export const updateProviderDocumentSchema = insertProviderDocumentSchema
  .omit({ createdAt: true, updatedAt: true })
  .partial()
  .required({ id: true });

// REMOVED: Old schemas for deleted tables (providerLicenses, providerInsurance, credentialDocuments, etc.)

// NPI provider creation schema
export const createProviderFromNPISchema = z.object({
  npiNumber: z
    .string()
    .length(10, 'NPI must be exactly 10 digits')
    .regex(/^\d{10}$/, 'NPI must contain only digits'),
});

export type CreateProviderFromNPIInput = z.infer<
  typeof createProviderFromNPISchema
>;

// Custom types for API responses
export type ProviderWithDetails = Provider & {
  documents: ProviderDocument[];
  clinics: (ClinicProvider & { clinic: Clinic })[];
  complianceStatus: {
    isBlocked: boolean;
    hasExpiringSoon: boolean; // within 90 days
    hasExpired: boolean;
    hasMissingMalpractice: boolean;
    blockingReasons: string[];
  };
};

export type ComplianceDashboardItem = {
  providerId: string;
  providerName: string;
  npiNumber: string | null;
  itemType: 'license' | 'malpractice_insurance' | 'document';
  itemId: string;
  itemDescription: string;
  expirationDate: string | null; // ISO 8601 date string
  daysUntilExpiration: number | null;
  status: 'expired' | 'expiring-soon' | 'missing' | 'ok';
  state?: string; // for licenses
};

export type ExpirationStatus =
  | 'expired'
  | 'expiring-30'
  | 'expiring-60'
  | 'expiring-90'
  | 'ok'
  | 'no-expiration';

export type ProviderSearchInput = {
  query?: string;
  status?: string;
  providerType?: string;
  limit?: number;
  offset?: number;
};

export type ProviderListItem = Pick<
  Provider,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'npiNumber'
  | 'providerType'
  | 'status'
  | 'lifecycleState'
> & {
  complianceStatus: ExpirationStatus;
  activeClinicsCount: number;
  isBlocked: boolean;
  blockingReasons: string[];
};

// ========== COMPONENT STATISTICS ==========

export type ComponentStatistics = {
  procedureId: string;
  ltsCode: string;
  cptCode: string | null;
  title: string;
  activeCount: number;
  inactiveCount: number;
  unknownCount: number;
  suspendedCount: number;
  notOnContractCount: number;
  totalClinics: number;
};

// ========== INTAKE FORM VERIFICATION ==========

export type ExtractedRow = {
  ltsCode: string;
  cptCode?: string; // CPT code from column 2 (e.g., "99213", "N/A")
  rate: number | null;
  selected: boolean;
  rowIndex: number;
  description?: string; // Component description from column 3
  confidence: number;
  detectionMethod?: string;
};

export type VerificationDataResponse = {
  jobId: string;
  contractId: string;
  fileName: string;
  pdfUrl: string;
  extractedRows: ExtractedRow[];
  status: 'processing' | 'completed' | 'failed';
  errorMessage: string | null;
};

// ========== ROSTER JOB TYPES ==========

export type RosterJob = InferSelectModel<typeof rosterJobs>;
export type NewRosterJob = InferInsertModel<typeof rosterJobs>;

export type { RosterExtractedProvider };

export type RosterVerificationDataResponse = {
  jobId: string;
  clinicId: string | null;
  groupId: number | null;
  fileName: string;
  pdfUrl: string;
  extractedProviders: RosterExtractedProvider[];
  status: 'processing' | 'completed' | 'failed';
  errorMessage: string | null;
};

export type RosterConfirmResult = {
  created: number;
  updated: number;
  linked: number;
  errors: string[];
};
